import { useEffect, useId, useRef, useState } from 'react'
import Icon from '../../docs/Icon'
import { addDays, addMonths, dateHints, formatDate, parseDate, sameDay, type DateFormat, type HintPlacement } from './dates'

export type DatePickerProps = {
  label: string
  hint?: string
  /** A date in the field's format, or empty. */
  value?: string
  /** Month first (MM/DD/YYYY, the default) or ISO 8601 (YYYY-MM-DD). */
  format?: DateFormat
  /** Where the format hint shows; above the field by default. */
  hintPlacement?: HintPlacement
  onChange?: (value: string) => void
  /** Show the calendar button. Typing always works. */
  calendar?: boolean
  /** The earliest date allowed. Earlier days are disabled in the calendar, and a typed earlier date is an error. */
  min?: Date
  /** The latest date allowed. */
  max?: Date
  id?: string
}

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * A date field: type the date, or open the calendar and move by day with the
 * arrow keys, by week with up and down, by month with Page Up and Page Down,
 * and to the start or end of the week with Home and End.
 */
export default function DatePicker({
  label,
  format = 'mdy',
  hint = dateHints[format],
  hintPlacement = 'above',
  value = '',
  onChange,
  calendar = true,
  min,
  max,
  id,
}: DatePickerProps) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const hintId = `${fieldId}-hint`
  const errorId = `${fieldId}-error`
  const [text, setText] = useState(value)
  const [touched, setTouched] = useState(false)
  const [open, setOpen] = useState(false)
  const [focus, setFocus] = useState(() => parseDate(value, format) ?? new Date())
  // A new value from the parent, such as a reset or saved data, replaces what is typed.
  const [given, setGiven] = useState(value)
  if (value !== given) {
    setGiven(value)
    setText(value)
    setTouched(false)
  }
  const grid = useRef<HTMLDivElement>(null)
  const toggle = useRef<HTMLButtonElement>(null)

  const outside = (date: Date) => (!!min && date < min) || (!!max && date > max)
  const parsed = parseDate(text, format)
  const selected = parsed && !outside(parsed) ? parsed : null
  const invalid = touched && text.trim() !== '' && !selected
  const clamp = (date: Date) => (min && date < min ? min : max && date > max ? max : date)

  // The focused day takes keyboard focus whenever it moves.
  useEffect(() => {
    if (open) grid.current?.querySelector<HTMLButtonElement>('[data-focused="true"]')?.focus()
  }, [open, focus])

  const commit = (next: string) => {
    setText(next)
    onChange?.(next)
  }

  const close = () => {
    setOpen(false)
    toggle.current?.focus()
  }

  const pick = (date: Date) => {
    commit(formatDate(date, format))
    setTouched(true)
    close()
  }

  const onGridKey = (event: React.KeyboardEvent) => {
    const moves: Record<string, () => Date> = {
      ArrowLeft: () => addDays(focus, -1),
      ArrowRight: () => addDays(focus, 1),
      ArrowUp: () => addDays(focus, -7),
      ArrowDown: () => addDays(focus, 7),
      PageUp: () => addMonths(focus, -1),
      PageDown: () => addMonths(focus, 1),
      Home: () => addDays(focus, -focus.getDay()),
      End: () => addDays(focus, 6 - focus.getDay()),
    }
    if (moves[event.key]) {
      event.preventDefault()
      setFocus(moves[event.key]())
    } else if (event.key === 'Escape') {
      event.preventDefault()
      close()
    }
  }

  const first = new Date(focus.getFullYear(), focus.getMonth(), 1)
  const start = addDays(first, -first.getDay())
  const days = Array.from({ length: 42 }, (_, i) => addDays(start, i))
  const weeks = Array.from({ length: 6 }, (_, w) => days.slice(w * 7, w * 7 + 7)).filter((week) =>
    week.some((day) => day.getMonth() === focus.getMonth()),
  )
  const title = `${months[focus.getMonth()]} ${focus.getFullYear()}`

  return (
    <div className="mds-field mds-date">
      <label className="mds-label" htmlFor={fieldId}>
        {label}
      </label>
      <span className={hintPlacement === 'placeholder' ? 'mds-visually-hidden' : 'mds-hint'} id={hintId}>
        {hint}
      </span>
      <div className="mds-date__control">
        <input
          id={fieldId}
          className="mds-input"
          inputMode="numeric"
          autoComplete="off"
          value={text}
          placeholder={hintPlacement === 'above' ? undefined : hint}
          aria-invalid={invalid}
          aria-describedby={`${hintId}${invalid ? ` ${errorId}` : ''}`}
          onChange={(event) => commit(event.target.value)}
          onBlur={() => setTouched(true)}
        />
        {calendar && (
          <button
            ref={toggle}
            type="button"
            className="mds-date__toggle"
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={selected ? `Choose date, ${formatDate(selected, format)} selected` : 'Choose date'}
            onClick={() => {
              setFocus(selected ?? clamp(new Date()))
              setOpen((state) => !state)
            }}
          >
            <Icon name="calendar" size={18} />
          </button>
        )}
      </div>
      {invalid && (
        <span className="mds-error-text" id={errorId}>
          {parsed && min && parsed < min
            ? `Enter a date on or after ${formatDate(min, format)}.`
            : parsed && max && parsed > max
              ? `Enter a date on or before ${formatDate(max, format)}.`
              : `Enter a real date as ${hint.toUpperCase()}.`}
        </span>
      )}
      {open && (
        <div className="mds-date__calendar" role="dialog" aria-modal="false" aria-label={`Choose date, ${title}`}>
          <div className="mds-date__head">
            <button type="button" className="mds-icon-button" aria-label="Previous month" onClick={() => setFocus(addMonths(focus, -1))}>
              <Icon name="chevron" size={16} className="mds-date__prev" />
            </button>
            <p className="mds-date__title" aria-live="polite">
              {title}
            </p>
            <button type="button" className="mds-icon-button" aria-label="Next month" onClick={() => setFocus(addMonths(focus, 1))}>
              <Icon name="chevron" size={16} />
            </button>
          </div>
          <div ref={grid} role="grid" aria-label={title} className="mds-date__grid" onKeyDown={onGridKey}>
            <div role="row" className="mds-date__row">
              {weekdays.map((day, i) => (
                <span key={day} role="columnheader" className="mds-date__weekday" aria-label={weekdayNames[i]}>
                  {day}
                </span>
              ))}
            </div>
            {weeks.map((week) => (
              <div key={week[0].toISOString()} role="row" className="mds-date__row">
                {week.map((day) => {
                  const otherMonth = day.getMonth() !== focus.getMonth()
                  const disabled = outside(day)
                  const isFocus = sameDay(day, focus)
                  const isSelected = !!selected && sameDay(day, selected)
                  return (
                    <span key={day.toISOString()} role="gridcell" aria-selected={isSelected}>
                      <button
                        type="button"
                        className={`mds-date__day${otherMonth ? ' is-outside' : ''}${disabled ? ' is-disabled' : ''}${isSelected ? ' is-selected' : ''}${sameDay(day, new Date()) ? ' is-today' : ''}`}
                        tabIndex={isFocus ? 0 : -1}
                        data-focused={isFocus}
                        aria-disabled={disabled || undefined}
                        aria-label={`${day.getDate()} ${months[day.getMonth()]} ${day.getFullYear()}, ${weekdayNames[day.getDay()]}`}
                        onClick={() => {
                          if (!disabled) pick(day)
                        }}
                      >
                        {day.getDate()}
                      </button>
                    </span>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
